'use client';

import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import SiteInquiryForm from '@/components/contact/SiteInquiryForm';
import { Button, H3, P } from '@/components/ui';

export type FeedbackFormDialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  formLabels: {
    submit: string;
    name: string;
    email: string;
    company: string;
    message: string;
  };
  closeLabel?: string;
};

export default function FeedbackFormDialog({
  open,
  onClose,
  title,
  subtitle,
  formLabels,
  closeLabel = 'Close',
}: FeedbackFormDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open) {
      if (!el.open) el.showModal();
      return;
    }
    if (el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className={clsx(
        'feedback-form-dialog',
        'fixed inset-0 z-[var(--z-modal,60)] m-0 h-full max-h-none w-full max-w-none',
        'border-0 bg-transparent p-0 backdrop:bg-black/75 backdrop:backdrop-blur-sm'
      )}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <div
        className="flex min-h-full items-end justify-center p-4 sm:items-center"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className="feedback-form-dialog__panel w-full max-w-lg"
          role="document"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <H3 className="m-0 text-lg font-semibold text-prose sm:text-xl">{title}</H3>
              <P className="m-0 mt-2 text-sm leading-relaxed text-prose-muted">{subtitle}</P>
            </div>
            <Button type="button" variant="ghost" className="shrink-0" onClick={onClose} aria-label={closeLabel}>
              ✕
            </Button>
          </div>
          <SiteInquiryForm
            className="page-inquiry-form mt-6"
            defaultType="feedback"
            labels={formLabels}
          />
        </div>
      </div>
    </dialog>
  );
}
